import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ExtLink } from '../components/ExtLink'
import { Term } from '../components/Term'
import { GLOSSARY } from '../lib/glossary'

export function Docs() {
  const { hash } = useLocation()

  useEffect(() => {
    if (!hash) return
    const id = hash.replace('#', '')
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [hash])

  return (
    <div className="max-w-3xl mx-auto py-6">
      <h1 className="text-2xl sm:text-3xl font-semibold text-text mb-2">Hakkında</h1>
      <p className="text-text-dim mb-8">
        Bu sayfa Sezi’nin ne yaptığını, nasıl çalıştığını ve sonuçların nasıl
        yorumlanması gerektiğini sade bir dille anlatır. Kullanılan veri kümeleri,
        kütüphaneler ve kaynaklar için{' '}
        <Link to="/tesekkur" className="text-accent">
          Teşekkürler sayfasına
        </Link>{' '}
        bakabilirsiniz.
      </p>

      <Section id="proje" title="Proje hakkında">
        <p>
          Sezi, kontrastlı meme MR görüntülerini otomatik olarak inceleyen bir araştırma
          prototipidir. Görüntüdeki lezyonu bulur, sınırlarını çıkarır, 3B yüzey
          modelini üretir ve lezyonun iyi huylu ya da kötü huylu olma olasılığına dair
          bir tahmin sunar.
        </p>
        <p>
          Bu prototip, Başkent Üniversitesi bünyesinde proje çalışması olarak
          geliştirilmiştir. Klinik kullanım için tasarlanmamış ve klinik onay
          sürecinden geçmemiştir.
        </p>
        <p className="border-l-2 border-warn pl-3 text-warn/90">
          Sezi’nin çıktıları tıbbi tavsiye değildir. Tanı veya tedavi kararlarında
          kullanılamaz. Şüpheli bir bulgu için her zaman yetkili bir hekime başvurunuz.
        </p>
      </Section>

      <Section id="nasil-calisir" title="Sezi nasıl çalışır?">
        <p>Görüntü işleme süreci üç adımdan oluşur:</p>
        <ol className="list-decimal pl-5 space-y-2">
          <li>
            <strong className="text-text"><Term id="segmentasyon">Segmentasyon</Term>.</strong>{' '}
            Bir derin öğrenme modeli
            (<ExtLink href="https://github.com/MIC-DKFZ/nnUNet">nnU-Net</ExtLink>)
            görüntüde lezyonun bulunduğu bölgeyi <Term id="voksel">voksel</Term> voksel
            işaretler. Çıktı, görüntüyle aynı boyutta bir maskedir: her voksel ya lezyon
            ya da arka plan olarak etiketlenir.
          </li>
          <li>
            <strong className="text-text">3B yüzey oluşturma.</strong> Maskeden,{' '}
            <Term id="mars-kupleri">yürüyen küpler</Term> (marching cubes) algoritmasıyla
            üçgenlerden oluşan bir yüzey çıkarılır
            (<ExtLink href="https://scikit-image.org/">scikit-image</ExtLink>). Yüzey,
            hacmi bozmayan <Term id="taubin">Taubin yumuşatma</Term> filtresinden
            geçirilir
            (<ExtLink href="https://github.com/mikedh/trimesh">trimesh</ExtLink>) ve{' '}
            <Term id="gltf">glTF</Term> biçiminde tarayıcıda görüntülenebilen bir 3
            boyutlu modele dönüştürülür.
          </li>
          <li>
            <strong className="text-text">Sınıflandırma.</strong> Maskenin merkezi
            etrafından bir bölge alınır. Bu bölgenin çekim öncesi ve sonrası farklı
            zamanlardaki yoğunluk değişimleri{' '}
            <Term id="kinetik-kanal">kinetik kanallar</Term> olarak ayrı bir derin
            öğrenme modeline verilir. Model, lezyonun kötü huylu olma olasılığını
            üretir.
          </li>
        </ol>
      </Section>

      <Section id="iki-model" title="Neden iki model var?">
        <p>
          Sezi iki farklı sınıflandırıcı sunar. İki modelin mimarisi aynıdır; fark,
          eğitimde gördükleri veri kümelerinden gelir.
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong className="text-text">AMBL modeli</strong> yalnızca{' '}
            <ExtLink href="https://www.cancerimagingarchive.net/collection/advanced-mri-breast-lesions/">
              AMBL veri kümesi
            </ExtLink>{' '}
            üzerinde eğitilmiştir. AMBL’ye benzer çekim protokolüne sahip görüntülerde en
            yüksek doğruluğu verir; bu kümenin dışındaki veri gruplarında
            doğrulanmamıştır.
          </li>
          <li>
            <strong className="text-text">Kombine model</strong> AMBL ve{' '}
            <ExtLink href="https://www.cancerimagingarchive.net/collection/breast-diagnosis/">
              BREAST-DIAGNOSIS
            </ExtLink>{' '}
            kümeleri birleştirilerek eğitilmiştir. Tek bir veri grubuna odaklanmaz;
            farklı kaynaklardan gelen görüntülerde daha tutarlı davranır, ancak AMBL
            üzerindeki doğruluğu biraz daha düşüktür.
          </li>
        </ul>
        <p>
          Vaka sayfasında her iki modelin sonucu yan yana gösterilir. İki sonuç
          arasındaki fark, modelin o görüntüde ne kadar kararlı olduğuna dair ek ipucu
          verir.
        </p>
      </Section>

      <Section id="veri-setleri" title="Veri setleri">
        <p>
          Eğitim ve değerlendirmede{' '}
          <ExtLink href="https://www.cancerimagingarchive.net/">
            The Cancer Imaging Archive (TCIA)
          </ExtLink>{' '}
          üzerinden açık erişimle yayımlanan iki veri kümesi kullanılmıştır:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong className="text-text">
              <ExtLink href="https://www.cancerimagingarchive.net/collection/advanced-mri-breast-lesions/">
                AMBL (Advanced-MRI-Breast-Lesions)
              </ExtLink>
              .
            </strong>{' '}
            99 hastadan toplanmış 165 lezyon içerir. Her lezyon için zaman serisi halinde
            5 fazlı kontrastlı MR görüntüsü ve uzman radyolog tarafından çizilmiş lezyon
            maskesi mevcuttur.
          </li>
          <li>
            <strong className="text-text">
              <ExtLink href="https://www.cancerimagingarchive.net/collection/breast-diagnosis/">
                BREAST-DIAGNOSIS
              </ExtLink>
              .
            </strong>{' '}
            45 hastadan oluşur; hasta başına 8 fazlı görüntü vardır. Lezyon maskeleri
            Sezi’nin kendi segmentasyon modeli tarafından otomatik üretilmiştir; uzman
            çizimi yoktur.
          </li>
        </ul>
        <p>
          Segmentasyon modelinin <Term id="on-egitim">ön eğitimi</Term> için ek olarak{' '}
          <Term id="mama-mia">MAMA-MIA</Term> (<ExtLink href="https://www.synapse.org/Synapse:syn60868042">Synapse</ExtLink>)
          veri kümesi kullanılmıştır. Bu küme yaklaşık 1500 hasta içeren çok merkezli
          açık erişimli kontrastlı meme MR koleksiyonudur ve segmentasyon modelinin
          temel öğrenmesini sağlamıştır.
        </p>
        <p>
          Modeller eğitim sırasında bu kümelerin sadece bir kısmını görür; kalan kısım
          test için kullanılır. Sayfada gösterilen tahminler, modelin o vakayı eğitimde
          görmediği bölümünden gelir; yani modelin gerçek anlamda yeni bir görüntüye
          tepkisini yansıtır.
        </p>
      </Section>

      <Section id="sayilar-ne-anlam" title="Sayılar ne anlama geliyor?">
        <p>
          Vaka sayfasında bir lezyon için iki olasılık çubuğu gösterilir; örneğin
          "%78 kötü huylu". Bu sayı modelin kendi tahminidir; gerçeğin %78 olduğu
          anlamına gelmez. Olasılık 0.5’e ne kadar yakınsa model o kadar kararsızdır.
        </p>
        <p>
          Yan tarafta ayrıca o vakanın gerçek etiketi (örnek: kötü huylu) gösterilir. Bu,
          patoloji raporlarından gelen kesin bilgidir ve sadece demo amaçlıdır;
          gerçek kullanımda model tek başına çalışır.
        </p>
        <p>
          <Term id="topluluk">Topluluk</Term> modunda sonuç 5 farklı eğitilmiş modelin
          ortalamasıdır; tek bir model değildir. Tahminlerin yanında her modelin ayrı
          sonucu da köşeli parantez içinde listelenir. Modeller arası fark fazlaysa
          belirsizlik yüksektir.
        </p>
        <p>
          Modellerin başarımı, 5 katlı <Term id="capraz-dogrulama">çapraz doğrulama</Term>
          {' '}ile ölçülen <Term id="auc">AUC</Term> değeri üzerinden raporlanır.
          AMBL modelinin AUC ortalaması yaklaşık 0.87, kombine modelin yaklaşık
          0.80’dir. Mükemmel ayrım 1.0, rastgele tahmin 0.5 olduğu için bu değerler
          modelin "iyi huylu olanları kötü huylulardan büyük ölçüde ayırt edebildiğini"
          gösterir; ancak %100 doğru olduğu anlamına gelmez.
        </p>
      </Section>

      <Section id="sinirlamalar" title="Bilinen sınırlamalar">
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong className="text-text">Veri kümesi küçük.</strong> Eğitim verisi
            yaklaşık 200 lezyondan oluşur. Bu, derin öğrenme için küçük bir kümedir ve
            sonuçların kararlılığını sınırlar.
          </li>
          <li>
            <strong className="text-text">Veri grupları arası performans düşüşü.</strong>{' '}
            AMBL’de iyi çalışan model, BREAST-DIAGNOSIS’ta belirgin biçimde başarısızlığa
            uğrayabilir. Farklı kaynaklı bir görüntüde model çıktısı 0.5 civarına
            yakınsayıp ayrım yapamayabilir.
          </li>
          <li>
            <strong className="text-text">Tam kaçırma vakaları.</strong> Bazı durumlarda
            segmentasyon modeli lezyonu tamamen kaçırır. Bu olduğunda 3B model
            üretilemez ve sınıflandırıcı yedek bir merkezden çalıştırılır; bu da güveni
            azaltır.
          </li>
          <li>
            <strong className="text-text">Kalibrasyon sorunu.</strong> Modelin ürettiği
            olasılıklar belirgin bir şekilde 0.5 civarında yığılır. Yani %70 olarak
            verilen bir tahmin, gerçek olasılığın da %70 olduğu anlamına gelmeyebilir;
            sıralama doğru ama mutlak sayı bir miktar yanıltıcı olabilir.
          </li>
        </ul>
      </Section>

      <Section id="daha-fazlasi" title="Daha derine inmek isteyenler">
        <p>
          Bu sayfa Sezi’yi sade biçimde anlatır. İş akışının tam teknik detayları,
          eğitim yapılandırması, denenip reddedilen yaklaşımlar, kat bazlı sayılar ve
          v2 yol haritası için{' '}
          <Link to="/teknik-detaylar" className="text-accent">Teknik detaylar</Link>{' '}
          sayfasına bakabilirsiniz. Kullanılan tüm açık veri kümeleri ve kütüphaneler
          için ise{' '}
          <Link to="/tesekkur" className="text-accent">Teşekkürler</Link> sayfası
          mevcuttur.
        </p>
      </Section>

      <Section id="sozluk" title="Sözlük">
        <p className="mb-4">
          Sayfalarda geçen teknik terimlerin kısa açıklamaları aşağıdadır.
        </p>
        <div className="space-y-4">
          {GLOSSARY.map((g) => (
            <div key={g.id} id={g.id} className="border-l-2 border-border pl-4">
              <h3 className="text-text font-medium mb-1">{g.term}</h3>
              <p className="text-text-dim text-sm leading-relaxed">{g.short}</p>
              {g.long && (
                <p className="text-text-dim text-sm leading-relaxed mt-2">{g.long}</p>
              )}
              {g.links && g.links.length > 0 && (
                <div className="mt-2 text-xs flex flex-wrap gap-x-3 gap-y-1">
                  {g.links.map((l) => (
                    <ExtLink key={l.href} href={l.href}>
                      {l.label}
                    </ExtLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>
    </div>
  )
}

function Section({
  id,
  title,
  children,
}: {
  id: string
  title: string
  children: React.ReactNode
}) {
  return (
    <section id={id} className="mb-10 scroll-mt-24">
      <h2 className="text-xl font-medium text-text mb-3 border-b border-border pb-2">
        {title}
      </h2>
      <div className="space-y-3 text-text-dim leading-relaxed">{children}</div>
    </section>
  )
}
